import { useCallback, useContext, useEffect, useState } from "react";
import { navigate } from "gatsby";
import { useLocation } from "@gatsbyjs/reach-router";
import { useAuth } from "@context/AuthProvider";
import { LuxeContext } from "@context/LuxeProvider";
import { getLuxeGiftInfo, LUXE_GIFT_ACCOUNT_TAG } from "@helpers/luxe";
import { removeQueryParam } from "@helpers/browser";
import { useDebounce } from "@hooks/useDebounce";

import { Popup } from "./Popup";
import LuxeGift from "./LuxeGift";

import CloseIcon from "@svgs/close-delete.svg";
import LuxeImage from "@images/luxe-logo-2.png";

import "./LuxeGiftPopup.scss";

// Show popup when ?showLuxeGiftPopup=true is in the URL, and the gift is not expired yet.
const LUXE_GIFT_POPUP_QUERY_PARAM = "showLuxeGiftPopup";

function LuxeGiftPopup() {
  const location = useLocation();
  const [luxeGiftPopupOpen, setLuxeGiftPopupOpen] = useState(false);
  const { accountDetails } = useAuth();
  const { luxe } = useContext(LuxeContext);

  const handleClose = useCallback(() => {
    setLuxeGiftPopupOpen(false);
    removeQueryParam(LUXE_GIFT_POPUP_QUERY_PARAM);
    navigate(window.location.href.split(window.location.origin)[1]);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const showPopup = searchParams.get(LUXE_GIFT_POPUP_QUERY_PARAM);
    const memberSince = luxe?.customerSubscription?.member_since;

    const luxeGiftPeriod = getLuxeGiftInfo(luxe, accountDetails?.tags);
    const hasGiftAlreadyBeenRedeemed = accountDetails?.tags?.some(
      (tag) => tag === LUXE_GIFT_ACCOUNT_TAG
    );

    if (
      showPopup === "true" &&
      memberSince &&
      luxeGiftPeriod?.status !== "expired" &&
      !hasGiftAlreadyBeenRedeemed &&
      luxe?.isLuxeActive &&
      !luxe.hasActiveOffer
    ) {
      setLuxeGiftPopupOpen(true);
    } else {
      setLuxeGiftPopupOpen(false);
    }
  }, [location.search, luxe, accountDetails]);

  const debouncedLuxeGiftPopupOpen = useDebounce(luxeGiftPopupOpen, 300);

  return (
    <Popup
      className="luxe-gift-popup"
      open={debouncedLuxeGiftPopupOpen}
      closeOnDocumentClick
      onClose={handleClose}
    >
      <button
        type="button"
        className="absolute top-[20px] right-[20px] z-10 focus:outline-none"
        onClick={handleClose}
        aria-label="Close popup"
      >
        <CloseIcon fill="black" width="15" height="15" />
      </button>
      <div className="ml-[20px] mt-[30px] w-[100px] desktop:w-[120px] desktop:ml-[30px]">
        <img src={LuxeImage} alt="AL Luxe Logo" loading="lazy" />
      </div>
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      {/* eslint-disable jsx-a11y/click-events-have-key-events */}
      <div
        className="px-[5px] pb-[5px]"
        onClick={(e) => {
          if (e.target.getAttribute("data-is-open-cart-button")) {
            // Close the popup when clicking the open cart button
            handleClose();
          }
        }}
      >
        <LuxeGift />
      </div>
    </Popup>
  );
}

export default LuxeGiftPopup;
